import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { type ChartLayout } from './layout';
import { LABEL_TEXT, VALUE_TEXT, chartFill, type ChartSeries } from './scale';

export type SplitRowsProps = {
  /** The parts, in the order every row shows them (two or three; work model uses remote, hybrid, onsite). */
  parts: Array<{ id: string; label: ReactNode; series: ChartSeries }>;
  /** One row per category; `values` in the parts' order. */
  rows: Array<{ id: string; label: ReactNode; values: number[] }>;
  /** The part the headline is about: its share is written at the end of each row. */
  focusPartId: string;
  /** Formats a share (0 to 100) for the page's language. */
  formatShare: (percent: number) => string;
  /** `auto` (default) switches to the narrow form in a narrow container: each label above its bar, the bar full width. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

const ROW: Record<ChartLayout, string> = {
  auto: 'flex flex-col gap-0.5 @md:flex-row @md:items-center @md:gap-3',
  narrow: 'flex flex-col gap-0.5',
  wide: 'flex items-center gap-3',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: '@md:w-1/4 @md:shrink-0',
  narrow: '',
  wide: 'w-1/4 shrink-0',
};
// In a column, flex-1 would collapse the bar line to no height: the narrow form sizes it by width.
const BAR_LINE: Record<ChartLayout, string> = { auto: '@md:flex-1', narrow: '', wide: 'flex-1' };

/** Split bars stacked per category on one 0 to 100 scale: how the mix differs between categories. */
export function SplitRows({
  parts,
  rows,
  focusPartId,
  formatShare,
  layout = 'auto',
  description,
  className,
}: SplitRowsProps) {
  devWarn(parts.length > 3, 'SplitRows shows at most three parts per row; more is a table.');
  const focus = parts.findIndex((p) => p.id === focusPartId);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      {rows.map((r) => {
        const total = r.values.reduce((s, v) => s + Math.max(0, v), 0) || 1;
        return (
          <div key={r.id} className={ROW[layout]}>
            <span className={cx('truncate', LABEL_TEXT, ROW_LABEL[layout])}>{r.label}</span>
            <span className={cx('flex min-w-0 items-center gap-3', BAR_LINE[layout])}>
              <span className="flex h-5 min-w-0 flex-1 origin-left gap-0.5 overflow-hidden rounded-jc-info motion-safe:animate-jc-grow-x">
                {parts.map((p, i) => (
                  <span
                    key={p.id}
                    className={chartFill(p.series)}
                    style={{ flexGrow: Math.max(0, r.values[i] ?? 0) / total, flexBasis: 0 }}
                  />
                ))}
              </span>
              <span className={cx('w-12 shrink-0 text-right', VALUE_TEXT)}>
                {formatShare(((r.values[focus] ?? 0) / total) * 100)}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The legend for SplitRows and SplitBar parts. */
export function PartsLegend({ parts }: { parts: SplitRowsProps['parts'] }) {
  return (
    <>
      {parts.map((p) => (
        <span key={p.id} className="inline-flex items-center gap-1.5">
          <span className={cx('size-2.5 rounded-jc-info', chartFill(p.series))} />
          {p.label}
        </span>
      ))}
    </>
  );
}
